// src/modules/app-views/customers/components/CustomersPreviewBody.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Typography, Flex, Divider, theme } from 'antd';
import { MailOutlined, PhoneOutlined, EnvironmentOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import translateX from 'utils/translateX';
import { getDateFormatByLocale } from 'utils/helpers';

const { Text } = Typography;

// *******************************************************************************
// STYLE CONSTANTS
// *******************************************************************************
const CONTAINER_STYLE = { width: '100%' };
const DIVIDER_STYLE = { margin: '8px 0' };
const ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 8 };
const ROW_STYLE_INDENT = { display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 20 };
const ICON_STYLE_BASE = { fontSize: 13, flexShrink: 0, lineHeight: 1 };
const LABEL_TEXT_STYLE = { fontSize: 12, whiteSpace: 'nowrap' };
const VALUE_TEXT_STYLE = { fontSize: 13 };

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const CustomersPreviewBody = ({ selectedDocument }) => {
  if (!selectedDocument) return null;

  const format = getDateFormatByLocale();

  const email = selectedDocument?.email?.value;
  const phone = selectedDocument?.phone?.national || selectedDocument?.phone?.value;

  const streetLine = [
    selectedDocument?.address?.address,
    selectedDocument?.address?.number,
    selectedDocument?.address?.complement,
  ]
    .filter(Boolean)
    .join(', ');

  const registeredAt = selectedDocument?._metadata?.audit?.createdAt;
  const registeredAtFormatted = registeredAt ? dayjs(registeredAt).format(format) : null;

  const hasContact = email || phone;
  const hasAddress = !!streetLine;

  return (
    <Flex vertical gap={4} style={CONTAINER_STYLE}>
      {hasContact && (
        <>
          <InfoRow icon={<MailOutlined />} value={email} />
          <InfoRow icon={<PhoneOutlined />} value={phone} />
          {hasAddress && <Divider style={DIVIDER_STYLE} />}
        </>
      )}
      {hasAddress && (
        <>
          <InfoRow icon={<EnvironmentOutlined />} value={streetLine} indent={!hasContact} />
          <Divider style={DIVIDER_STYLE} />
        </>
      )}
      <InfoRow
        icon={<CalendarOutlined />}
        label={translateX('registered_on')}
        value={registeredAtFormatted}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CustomersPreviewBody;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function InfoRow({ icon, label, value, indent }) {
  const { token } = theme.useToken();
  if (!value) return null;

  const iconStyle = { ...ICON_STYLE_BASE, color: token.colorTextSecondary };

  return (
    <div style={indent ? ROW_STYLE_INDENT : ROW_STYLE}>
      {icon && <span style={iconStyle}>{icon}</span>}
      {label && (
        <Text type="secondary" style={LABEL_TEXT_STYLE}>
          {label}:
        </Text>
      )}
      <Text style={VALUE_TEXT_STYLE}>{value}</Text>
    </div>
  );
}
